import React, {useEffect, useState, useRef, useMemo, useCallback} from "react";
import "./LiveStream.css";

import StreamFilter from "./StreamFilter";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";

import BootstrapTable from 'react-bootstrap-table-next';
import Badge from "react-bootstrap/Badge";
import {tagColor, getBondColor, stringToBondThemeColor} from "../../../../../../lib/ui/colorUtils.js";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronRight, faUser} from "@fortawesome/free-solid-svg-icons";
import DefaultEvent from "./Events/DefaultEvent/index.jsx";
import MqttEvent from "./Events/MqttEvent/index.jsx";
import CloudWatchEvent from "./Events/CloudWatchEvent/index.jsx";
import UserEvent from "./Events/UserEvent/index.jsx";
import { IntegrationsSourceCell } from "./Events/UserEvent/IntegrationsEvent.jsx";
import { HomeSourceCell } from "./Events/UserEvent/HomeEvent.jsx";
import TimestampBadge from "../../../Partials/TimestampBadge";
import { useTimezone } from "../../../../contexts/TimezoneContext";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Register chart components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Plugin to highlight visible time range
const highlightVisibleRangePlugin = {
    id: 'highlightVisibleRange',
    beforeDatasetsDraw: (chart, args, options) => {
        if (!options.visibleTimeRange || !options.sortedTimes) {
            return;
        }

        const { ctx, chartArea, scales } = chart;
        const xScale = scales.x;
        const visibleTimeRange = options.visibleTimeRange;
        const sortedTimes = options.sortedTimes;

        if (!sortedTimes || sortedTimes.length === 0) return;

        // Convert visible timestamps to label indices using actual timestamps
        let startIndex = -1;
        let endIndex = -1;

        // Add tolerance of 1 hour (3600000ms) to catch edge buckets
        const hourInMs = 3600000;

        // sortedTimes now contains UTC timestamps in milliseconds
        sortedTimes.forEach((timestamp, idx) => {
            // timestamp is already in milliseconds, no need to convert
            const time = timestamp;

            // Include bucket if it overlaps with the visible range (±1 hour tolerance)
            if (time >= visibleTimeRange.min - hourInMs && time <= visibleTimeRange.max + hourInMs) {
                if (startIndex === -1) {
                    startIndex = idx;
                }
                endIndex = idx;
            }
        });

        // If no bars found in range, don't draw highlight
        if (startIndex === -1 || endIndex === -1) return;

        // Extend the highlight by one hour bucket on each side to cover inconsistencies
        startIndex = Math.max(0, startIndex - 1);
        endIndex = Math.min(sortedTimes.length - 1, endIndex + 1);

        // Get bar width to extend highlight to outer edges
        const barWidth = xScale.width / sortedTimes.length;
        const xStart = xScale.getPixelForValue(startIndex) - (barWidth / 2);
        const xEnd = xScale.getPixelForValue(endIndex) + (barWidth / 2);

        // Draw highlighted region extending to outer edges of bars (5px taller than chart)
        ctx.save();
        ctx.fillStyle = 'rgba(110, 155, 250, 0.20)'; // Saturated blue, less opaque
        ctx.fillRect(xStart, chartArea.top - 2, xEnd - xStart, chartArea.bottom - chartArea.top + 5);
        ctx.restore();
    }
};

ChartJS.register(highlightVisibleRangePlugin);


const DEFAULT_FILTERS = [];

// Debug level: applies a quick preset filter on top of badge filters
// 'minimal'  – only user events
// 'debug'    – exclude noisy MQTT (m=0 heartbeats, root-topic publishes)
// 'all'      – no preset filtering
const DEBUG_LEVEL_FILTER = {
    minimal: (event) => {
        if (event.source !== 'user') return false;
        if (event.payload?.type === 'INTEGRATIONS' && event.payload?.action === 'REPORT') return false;
        return true;
    },
    debug: (event) => {
        if (event.payload?.m === 0) return false;
        if (event.payload?.topic === '/') return false;
        if (event.payload?.type === 'INTEGRATIONS' && event.payload?.action === 'REPORT') return false;
        return true;
    },
    all: () => true,
};

function getAttributeValue(event, attribute) {
    switch (attribute) {
        case 'source': return event.source ?? '';
        case 'action': return event.action ?? '';
        case 'bond_id': return event.bond_id ?? '';
        case 'payload.m': return String(event.payload?.m ?? '');
        case 'payload.topic': return event.payload?.topic ?? '';
        case 'payload.s': return String(event.payload?.s ?? '');
        case 'payload.i': return event.payload?.i ?? '';
        default: return event.payloadStr ?? '';
    }
}

function matchesFilter(event, filter) {
    if (!filter.enabled || !filter.query) return true;
    const value = String(getAttributeValue(event, filter.attribute)).toLowerCase();
    let matches;
    if (filter.is_regex) {
        try { matches = new RegExp(filter.query, 'i').test(value); }
        catch { matches = false; }
    } else {
        matches = value.includes(filter.query.toLowerCase());
    }
    return filter.is_negative ? !matches : matches;
}


function LiveStream({events, eventsByRequestId, bondsData, bondIds, homeMembers, homes, userId, onDateRangeFilter}) {
    const { timezone, formatDateCustom } = useTimezone();
    const shownDatesRef = useRef(new Set());
    const [filters, setFilters] = useState(() => {
        try {
            const saved = localStorage.getItem('streamFilterBadges');
            if (saved) return JSON.parse(saved);
        } catch { /* ignore */ }
        return DEFAULT_FILTERS;
    });
    const [debugLevel, setDebugLevel] = useState(() =>
        localStorage.getItem('streamDebugLevel') || 'debug'
    );
    const [queryLimit, setQueryLimit] = useState(100);

    // Helper function to get date parts in the selected timezone
    // Returns {year, month, day, hours, minutes} all in the user's selected timezone
    const getDatePartsInTimezone = useCallback((timestamp) => {
        const date = new Date(timestamp);
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        const parts = formatter.formatToParts(date);
        const result = {};
        parts.forEach(part => {
            if (part.type !== 'literal') {
                result[part.type] = part.value;
            }
        });

        return {
            year: result.year,
            month: result.month,
            day: result.day,
            hours: result.hour,
            minutes: result.minute
        };
    }, [timezone]);
    const [windowStart, setWindowStart] = useState(0);
    const tableContainerRef = useRef(null);
    const prevBondIdsRef = useRef();

    const WINDOW_SIZE = 500;
    const SCROLL_BATCH = 200;

    // Reset filters when bondIds change (user change)
    useEffect(() => {
        const prevBondIds = prevBondIdsRef.current;
        const currentBondIds = bondIds?.join(',') || '';

        if (prevBondIds && prevBondIds !== currentBondIds) {
            // Reset query limit to default
            setQueryLimit(100);
        }

        prevBondIdsRef.current = currentBondIds;
    }, [bondIds]);

    // Persist filters and debug level to localStorage
    useEffect(() => {
        try { localStorage.setItem('streamFilterBadges', JSON.stringify(filters)); } catch { /* ignore */ }
    }, [filters]);

    useEffect(() => {
        try { localStorage.setItem('streamDebugLevel', debugLevel); } catch { /* ignore */ }
    }, [debugLevel]);

    const handleAddFilter = useCallback((filter) => {
        setFilters(prev => [...prev, filter]);
    }, []);

    const handleRemoveFilter = useCallback((id) => {
        setFilters(prev => prev.filter(f => f.id !== id));
    }, []);

    const handleToggleFilter = useCallback((id) => {
        setFilters(prev => prev.map(f => f.id === id ? { ...f, enabled: !f.enabled } : f));
    }, []);

    // Filter events: debug level preset first, then all active badge filters (AND logic)
    const allFilteredEvents = useMemo(() => {
        const levelFn = DEBUG_LEVEL_FILTER[debugLevel] || (() => true);
        return events.filter(event =>
            levelFn(event) && filters.every(filter => matchesFilter(event, filter))
        );
    }, [events, filters, debugLevel]);

    // Apply sliding window to get visible events for table (max 500)
    const visibleEvents = allFilteredEvents.slice(windowStart, windowStart + WINDOW_SIZE);

    // Reset window when filters or debug level change
    useEffect(() => {
        setWindowStart(0);
    }, [filters, debugLevel]);

    // Wrapper to include queryLimit in date range filter
    const handleDateRangeFilter = async (startDate, endDate) => {
        if (onDateRangeFilter) {
            await onDateRangeFilter(startDate, endDate, queryLimit);
        }
    };

    // Handle scroll to load more events (sliding window) - using window scroll
    const handleScroll = useCallback(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        const scrolledToBottom = documentHeight - scrollTop - windowHeight < 200;

        if (scrolledToBottom && windowStart + WINDOW_SIZE < allFilteredEvents.length) {
            // User scrolled to bottom and there are more events
            // Move window forward by SCROLL_BATCH and remove SCROLL_BATCH from top
            const newStart = Math.min(windowStart + SCROLL_BATCH, allFilteredEvents.length - WINDOW_SIZE);
            setWindowStart(Math.max(0, newStart));
        }
    }, [windowStart, allFilteredEvents.length]);

    // Add window scroll listener
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Prepare chart data (using ALL filtered events, not just visible window)
    const chartData = useMemo(() => {
        // Group events by hour and bond_id
        // Store both the display key and the actual UTC timestamp for each hour bucket
        const groupedData = {};
        const hourKeyToTimestamp = {}; // Map from display key to UTC timestamp

        allFilteredEvents.forEach(event => {
            if (!event.timestamp || !event.bond_id) return;

            // Get the event's timestamp in UTC
            const eventTime = new Date(event.timestamp);

            // Round to hour in the selected timezone
            const parts = getDatePartsInTimezone(event.timestamp);
            const hourKey = `${parts.year}-${parts.month}-${parts.day}T${parts.hours}:00:00`;

            if (!groupedData[hourKey]) {
                groupedData[hourKey] = {};
                // Store the actual UTC timestamp for this hour bucket
                // This represents the start of this hour in the user's selected timezone
                hourKeyToTimestamp[hourKey] = eventTime.getTime();
            }

            if (!groupedData[hourKey][event.bond_id]) {
                groupedData[hourKey][event.bond_id] = 0;
            }

            groupedData[hourKey][event.bond_id]++;
        });

        // Sort time keys and get their corresponding UTC timestamps
        const sortedKeys = Object.keys(groupedData).sort();
        const sortedTimes = sortedKeys.map(key => hourKeyToTimestamp[key]);

        // Get all unique bond IDs
        const allBondIds = [...new Set(allFilteredEvents.map(e => e.bond_id).filter(Boolean))];

        // Prepare datasets for each bond (use sortedKeys to access groupedData)
        const datasets = allBondIds.map(bondId => {
            const color = getBondColor(bondId);
            return {
                label: bondId,
                data: sortedKeys.map(key => groupedData[key][bondId] || 0),
                backgroundColor: color,
                borderColor: color,
                borderWidth: 1
            };
        });

        // Format labels to show date and hour (without seconds) in selected timezone
        // Use sortedKeys since they already contain the timezone-aware hour string
        const labels = sortedKeys.map(key => {
            const parts = key.match(/(\d+)-(\d+)-(\d+)T(\d+):(\d+):(\d+)/);
            if (!parts) return key;
            return `${parts[2]}/${parts[3]} ${parts[4]}:${parts[5]}`;
        });

        return {
            labels,
            datasets,
            sortedTimes // Include original timestamps for click handling
        };
    }, [allFilteredEvents, formatDateCustom, getDatePartsInTimezone]);

    // Calculate datetime range of all events in memory
    const eventDateRange = useMemo(() => {
        if (allFilteredEvents.length === 0) return null;

        const timestamps = allFilteredEvents.map(e => new Date(e.timestamp).getTime());
        const minTime = Math.min(...timestamps);
        const maxTime = Math.max(...timestamps);

        return {
            first: formatDateCustom(new Date(minTime).toISOString(), 'yyyy-MM-dd HH:mm'),
            last: formatDateCustom(new Date(maxTime).toISOString(), 'yyyy-MM-dd HH:mm')
        };
    }, [allFilteredEvents, formatDateCustom]);

    // Calculate visible time range for chart highlighting
    const visibleTimeRange = useMemo(() => {
        // If all events fit in the table (<=500), highlight entire graph
        if (allFilteredEvents.length <= WINDOW_SIZE) {
            if (allFilteredEvents.length === 0) return null;
            const timestamps = allFilteredEvents.map(e => new Date(e.timestamp).getTime());
            return {
                min: Math.min(...timestamps),
                max: Math.max(...timestamps)
            };
        }

        // Otherwise, only highlight the visible window
        if (visibleEvents.length === 0) return null;
        const timestamps = visibleEvents.map(e => new Date(e.timestamp).getTime());
        return {
            min: Math.min(...timestamps),
            max: Math.max(...timestamps)
        };
    }, [visibleEvents, allFilteredEvents, WINDOW_SIZE]);

    // Handle chart click to jump to events
    const handleChartClick = useCallback((event, activeElements, chart) => {
        if (!chartData.sortedTimes || chartData.sortedTimes.length === 0) return;

        // Get the x-scale
        const xScale = chart.scales.x;

        // Get the click position
        const canvasPosition = {
            x: event.x !== undefined ? event.x : event.offsetX,
            y: event.y !== undefined ? event.y : event.offsetY
        };

        // Get the data index from the click position
        const dataX = xScale.getValueForPixel(canvasPosition.x);

        if (dataX !== undefined && dataX >= 0 && dataX < chartData.sortedTimes.length) {
            const clickedLabelIndex = Math.round(dataX);
            // sortedTimes now contains UTC timestamps in milliseconds
            const clickedTime = chartData.sortedTimes[clickedLabelIndex];

            // Check if events are in descending order (newest first)
            const firstEventTime = allFilteredEvents.length > 0 ? new Date(allFilteredEvents[0].timestamp).getTime() : 0;
            const lastEventTime = allFilteredEvents.length > 1 ? new Date(allFilteredEvents[allFilteredEvents.length - 1].timestamp).getTime() : 0;
            const isDescending = firstEventTime > lastEventTime;

            // Find the closest event to this timestamp
            let targetIndex = -1;
            let minDiff = Infinity;

            for (let i = 0; i < allFilteredEvents.length; i++) {
                const eventTime = new Date(allFilteredEvents[i].timestamp).getTime();
                const diff = Math.abs(eventTime - clickedTime);

                // If this event falls within the clicked hour (±30 minutes)
                if (diff <= 30 * 60 * 1000) {
                    if (diff < minDiff) {
                        minDiff = diff;
                        targetIndex = i;
                    }
                }
            }

            // If no event found within the hour, find the closest one
            if (targetIndex === -1) {
                for (let i = 0; i < allFilteredEvents.length; i++) {
                    const eventTime = new Date(allFilteredEvents[i].timestamp).getTime();
                    const diff = Math.abs(eventTime - clickedTime);
                    if (diff < minDiff) {
                        minDiff = diff;
                        targetIndex = i;
                    }
                }
            }

            if (targetIndex !== -1) {
                // Set windowStart to show this event at the top
                const newStart = Math.max(0, Math.min(targetIndex, allFilteredEvents.length - WINDOW_SIZE));
                setWindowStart(newStart);

                // Scroll to the table
                setTimeout(() => {
                    if (tableContainerRef.current) {
                        const tableTop = tableContainerRef.current.offsetTop - 100;
                        window.scrollTo({ top: tableTop, behavior: 'smooth' });
                    }
                }, 100);
            }
        }
    }, [chartData.sortedTimes, allFilteredEvents, WINDOW_SIZE]);

    const chartOptions = useMemo(() => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    stacked: true,
                    title: {
                        display: false
                    }
                },
                y: {
                    stacked: true,
                    title: {
                        display: false
                    },
                    beginAtZero: true
                }
            },
            onClick: handleChartClick,
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        boxWidth: 12,
                        padding: 10
                    }
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        title: (tooltipItems) => {
                            if (!tooltipItems || tooltipItems.length === 0) return '';
                            const index = tooltipItems[0].dataIndex;
                            if (!chartData.sortedTimes || index >= chartData.sortedTimes.length) return '';

                            const timestamp = chartData.sortedTimes[index];
                            const parts = getDatePartsInTimezone(timestamp);
                            return `${parts.month}/${parts.day}/${parts.year} ${parts.hours}:${parts.minutes}`;
                        }
                    }
                },
                highlightVisibleRange: {
                    visibleTimeRange: visibleTimeRange,
                    sortedTimes: chartData.sortedTimes
                }
            }
        };
    }, [visibleTimeRange, handleChartClick, allFilteredEvents.length, chartData.sortedTimes, WINDOW_SIZE, getDatePartsInTimezone]);

    function cellTimestampFormatter(cellValue, row, rowIndex, formatExtraData) {
        // Reset shown dates on first row to start fresh for each render
        if (rowIndex === 0) {
            shownDatesRef.current.clear();
        }

        // Convert UTC timestamp to selected timezone
        const date = new Date(cellValue);

        // Format date part in selected timezone
        const currentDate = new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: timezone
        }).format(date);

        // Check if we've already shown this date in the current render
        const shouldShowDate = !shownDatesRef.current.has(currentDate);
        if (shouldShowDate) {
            shownDatesRef.current.add(currentDate);
        }

        // Format time part in selected timezone with milliseconds
        const timeString = new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            fractionalSecondDigits: 3,
            timeZone: timezone,
            hour12: false
        }).format(date);

        return <div className="small">
            {shouldShowDate && (
                <Badge className={"w-100 fw-bold mb-1"} bg={"light"} text={"dark"} style={{fontSize: '0.85rem'}}>{currentDate}</Badge>
            )}
            <TimestampBadge
                value={cellValue}
                absolute={timeString}
                bg={"secondary"}
                className={"w-100 fw-light d-flex align-items-center justify-content-center"}
                style={{fontSize: '0.9rem', minHeight: '32px'}}
            />
        </div>
    }

    function sourceActionFormatter(cellValue, row, rowIndex, formatExtraData) {
        const bondColor = row.bond_id ? getBondColor(row.bond_id) : null;

        // Determine action badge CSS custom properties for connect/disconnect events
        const getActionBadgeStyle = (action) => {
            if (action === "connected" || action === "DEVICE_ADD") {
                return { '--integration-badge-bg': '#198754', '--integration-badge-color': '#ffffff', fontSize: '0.75rem' };
            } else if (action === "disconnected" || action === "DEVICE_DELETE") {
                return { '--integration-badge-bg': '#dc3545', '--integration-badge-color': '#ffffff', fontSize: '0.75rem' };
            } else if (action === "DEVICE_UPDATE") {
                return { '--integration-badge-bg': '#ca8a04', '--integration-badge-color': '#ffffff', fontSize: '0.75rem' };
            } else if (action === "OWNERSHIP_UPDATE") {
                return { '--integration-badge-bg': '#ea6f1e', '--integration-badge-color': '#ffffff', fontSize: '0.75rem' };
            } else if (action === "message") {
                return { '--integration-badge-bg': '#1d4ed8', '--integration-badge-color': '#ffffff', fontSize: '0.75rem' };
            }
            const bg = stringToBondThemeColor(action || 'default', 220);
            return { '--integration-badge-bg': bg, '--integration-badge-color': '#ffffff', fontSize: '0.75rem' };
        };

        if (row.source === "user") {
            const userBondId = row.payload?.attributes?.bond_id || row.bond_id || null;
            const userBondColor = userBondId ? getBondColor(userBondId) : null;

            if (row.payload?.type === 'CLOUDDEVICES') {
                const detected = row.payload?.attributes?.detected_remotes;
                const hasDetections = Array.isArray(detected) && detected.length > 0;
                return <div className="small">
                    {/* Line 1: User + Bond ID — 50/50 */}
                    <div className="d-flex w-100 gap-1 mb-1">
                        <Badge className="integrationBadge" style={{'--integration-badge-bg': '#8da9c4', '--integration-badge-color': '#1c2b3a', fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>
                            <FontAwesomeIcon icon={faUser} className="me-1" size="xs"/>User
                        </Badge>
                        {userBondId && (
                            <Badge className="bondBadge" style={{'--bond-badge-bg': userBondColor, color: '#ffffff', fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>{userBondId}</Badge>
                        )}
                    </div>
                    {/* Line 2: remotes > success/failed — 100% width, chevron fixed 10px, badges 50/50 */}
                    {Array.isArray(detected) && (
                        <div className="d-flex align-items-center w-100">
                            <Badge className="integrationBadge" style={{'--integration-badge-bg': '#0e7490', '--integration-badge-color': '#fff', fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>remotes</Badge>
                            <span style={{flex: '0 0 10px', textAlign: 'center', fontSize: '0.65rem', color: '#6c757d'}}>›</span>
                            <Badge bg={hasDetections ? 'success' : 'danger'} pill style={{fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>
                                {hasDetections ? 'success' : 'failed'}
                            </Badge>
                        </div>
                    )}
                </div>
            }

            // Gateway events (gw-up / gw-down) — show mqtt › gateway like MQTT source cell
            const isGateway = row.payload?.action === 'up' || row.payload?.action === 'down' ||
                row.payload?.direction?.startsWith('gw-') ||
                row.payload?.event?.direction?.startsWith('gw-') ||
                row.payload?.topic?.includes('/gw-up/') || row.payload?.topic?.includes('/gw-down/');

            if (isGateway) {
                return <div className="small">
                    {userBondId && (
                        <Badge className={"w-100 mb-1 bondBadge"} style={{'--bond-badge-bg': userBondColor, color: '#ffffff', fontSize: '0.75rem'}}>{userBondId}</Badge>
                    )}
                    <div className="d-flex align-items-center w-100">
                        <Badge className="fw-light" style={{...tagColor('mqtt', 'livestream'), fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>mqtt</Badge>
                        <span style={{flex: '0 0 10px', textAlign: 'center', fontSize: '0.65rem', color: '#6c757d'}}>›</span>
                        <Badge className="fw-lighter" pill style={{fontSize: '0.75rem', flex: 1, textAlign: 'center', background: '#1f6feb', color: '#fff'}}>gateway</Badge>
                    </div>
                </div>
            }

            // INTEGRATIONS events (EXEC / SYNC)
            if (row.payload?.type === 'INTEGRATIONS') {
                return <IntegrationsSourceCell row={row} />;
            }

            // HOME events (CREATED / INVITE_CREATED / INVITE_CANCELLED / …)
            if (row.payload?.type === 'HOME') {
                return <HomeSourceCell row={row} />;
            }

            return <div className="small">
                {/* User + Bond ID — 50/50 */}
                <div className="d-flex w-100 gap-1 mb-1">
                    <Badge className="integrationBadge" style={{'--integration-badge-bg': '#8da9c4', '--integration-badge-color': '#1c2b3a', fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>
                        <FontAwesomeIcon icon={faUser} className="me-1" size="xs"/>User
                    </Badge>
                    {userBondId && (
                        <Badge className="bondBadge" style={{'--bond-badge-bg': userBondColor, color: '#ffffff', fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>{userBondId}</Badge>
                    )}
                </div>
                {row.action && (
                    <Badge className="w-100 integrationBadge" style={{...getActionBadgeStyle(row.action), fontSize: '0.75rem'}}>{row.action}</Badge>
                )}
            </div>
        }

        if (row.source === "livelog" && row.bond_id) {
            return <div className="small">
                <Badge className={"w-100 bondBadge"} style={{'--bond-badge-bg': bondColor, color: '#ffffff', fontSize: '0.75rem'}}>{row.bond_id}</Badge>
            </div>
        }

        if (row.source === "mqtt-gateway") {
            const bondColor = row.bond_id ? getBondColor(row.bond_id) : null;
            return <div className="small">
                {row.bond_id && (
                    <Badge className={"w-100 mb-1 bondBadge"} style={{'--bond-badge-bg': bondColor, color: '#ffffff', fontSize: '0.75rem'}}>{row.bond_id}</Badge>
                )}
                <div className="d-flex align-items-center w-100">
                    <Badge className="fw-light" style={{...tagColor('mqtt', 'livestream'), fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>mqtt</Badge>
                    <span style={{flex: '0 0 10px', textAlign: 'center', fontSize: '0.65rem', color: '#6c757d'}}>›</span>
                    <Badge className="fw-lighter integrationBadge" pill style={{'--integration-badge-bg': '#1f6feb', '--integration-badge-color': '#fff', fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>gateway</Badge>
                </div>
            </div>
        }

        if (row.source === "mqtt" && row.bond_id) {
            const mqttBondId = row.payload?.attributes?.bond_id || row.bond_id;
            const mqttBondColor = getBondColor(mqttBondId);
            return <div className="small">
                <Badge className={"w-100 mb-1 bondBadge"} style={{'--bond-badge-bg': mqttBondColor, color: '#ffffff', fontSize: '0.75rem'}}>{mqttBondId}</Badge>
                <div className="d-flex align-items-center w-100">
                    <Badge className="fw-light" style={{...tagColor(row.source, "livestream"), fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>{row.source}</Badge>
                    <span style={{flex: '0 0 10px', textAlign: 'center', fontSize: '0.65rem', color: '#6c757d'}}>›</span>
                    <Badge className="fw-lighter integrationBadge" pill={true} style={{...getActionBadgeStyle(row.action), flex: 1, textAlign: 'center'}}>
                        {(row.action === 'gw-up' || row.action === 'gw-down') ? 'gateway' : row.action}
                    </Badge>
                </div>
            </div>
        }

        return <div className="small">
            <div className="d-flex align-items-center w-100">
                <Badge className="fw-light" style={{...tagColor(row.source, "livestream"), fontSize: '0.75rem', flex: 1, textAlign: 'center'}}>{row.source}</Badge>
                <span style={{flex: '0 0 10px', textAlign: 'center', fontSize: '0.65rem', color: '#6c757d'}}>›</span>
                <Badge className="fw-lighter integrationBadge" pill={true} style={{...getActionBadgeStyle(row.action), flex: 1, textAlign: 'center'}}>{row.action}</Badge>
            </div>
        </div>
    }

    function cellPayloadFormatter(cellValue, row, rowIndex, formatExtraData) {
        let content;

        // Check if this is a CloudWatch lifecycle event (has eventType field)
        if (row.payload?.eventType && (row.payload.eventType === "connected" || row.payload.eventType === "disconnected")) {
            content = <CloudWatchEvent event={row} />;
        } else if (row.source === "mqtt") {
            content = <MqttEvent event={row} eventsByRequestId={eventsByRequestId} bondsData={bondsData} homeMembers={homeMembers} dashboardUserId={userId}/>;
        } else if (row.source === "cloudwatch") {
            content = <CloudWatchEvent event={row} />;
        } else if (row.source === "livelog") {
            content = <div className="d-flex align-items-center gap-2">
                <Badge className={"fw-light flex-shrink-0"} style={{...tagColor("livelog", "livestream"), fontSize: '0.75rem'}}>livelog</Badge>
                <span className="small text-muted font-monospace">{row.payload?.message || JSON.stringify(row.payload)}</span>
            </div>;
        } else if (row.source === "mqtt-gateway") {
            // Inner MQTT data lives in payload.event — remap so MqttEvent can render it normally
            content = <MqttEvent
                event={{ ...row, action: 'message', payload: row.payload?.event || {} }}
                eventsByRequestId={eventsByRequestId}
                bondsData={bondsData}
                homeMembers={homeMembers}
                dashboardUserId={userId}
            />;
        } else if (row.source === "user") {
            content = <UserEvent event={row} eventsByRequestId={eventsByRequestId} bondsData={bondsData} homeMembers={homeMembers} homes={homes}/>;
        } else {
            content = <DefaultEvent event={row}/>;
        }

        return <div className="payloadScrollWrapper">{content}</div>;
    }


    const eventStreamHeader = [{
        dataField: 'timestamp',
        text: (<h4>Timestamp</h4>),
        headerClasses: ["colHeaderStreamTimestamp"],
        sort: true,
        classes: ["colStreamTimestamp"],
        formatter: cellTimestampFormatter
    }, {
        dataField: 'sourceAction',
        text: <h4>Source</h4>,
        classes: ["colStreamSource"],
        headerClasses: ["colHeaderStreamSource"],
        sort: false,
        formatter: sourceActionFormatter
    }, {
        dataField: 'payloadStr',
        text: <h4>Payload</h4>,
        classes: ["colStreamPayload"],
        headerClasses: ["colHeaderStreamPayload"],
        formatter: cellPayloadFormatter
    }]

    return (<div>
        {/* Only show filter when there's active bond data */}
        {bondIds && bondIds.length > 0 && (
            <Row className={"pb-2 overflow-hidden"}>
                <StreamFilter
                    filters={filters}
                    onAddFilter={handleAddFilter}
                    onRemoveFilter={handleRemoveFilter}
                    onToggleFilter={handleToggleFilter}
                    debugLevel={debugLevel}
                    onDebugLevelChange={setDebugLevel}
                    onDateRangeFilter={handleDateRangeFilter}
                    bondIds={bondIds}
                    queryLimit={queryLimit}
                    onQueryLimitChange={(v) => setQueryLimit(Number(v))}
                />
            </Row>
        )}

        {/* Total Events Count - only show when there's bond data */}
        {bondIds && bondIds.length > 0 && (
            <Row>
                <div className="text-muted small text-center">
                    Total events: <strong>{allFilteredEvents.length}</strong>
                    {eventDateRange && (
                        <span className="ms-2">({eventDateRange.first} → {eventDateRange.last})</span>
                    )}
                    {allFilteredEvents.length > WINDOW_SIZE && (
                        <span className="ms-2">(showing {windowStart + 1}-{Math.min(windowStart + WINDOW_SIZE, allFilteredEvents.length)})</span>
                    )}
                </div>
            </Row>
        )}

        {/* Event Count Chart */}
        {allFilteredEvents.length > 0 && chartData.labels.length > 0 && (
            <Row className={"mb-3"}>
                <div style={{height: '250px', width: '100%'}}>
                    <Bar data={chartData} options={chartOptions} />
                </div>
            </Row>
        )}

        <Row>
            <div ref={tableContainerRef}>
                {visibleEvents.length > 0 && <BootstrapTable keyField={"id"}
                                                      data={visibleEvents}
                                                      columns={eventStreamHeader}
                                                      classes={["liveStreamTable table table-hover table-sm table-borderless"]}
                                                      defaultSorted={[{dataField: 'timestamp', order: 'desc'}]}
                                                      headerClasses="d-none"
                />}
            </div>
        </Row>
    </div>);
}

export default LiveStream;
